import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface KioskDevice {
  id: string;
  organization_id: string;
  location_id: string;
  name: string;
  device_code: string;
  device_token: string | null;
  is_active: boolean;
  last_active_at: string | null;
  settings: KioskSettings;
  created_at: string;
  updated_at: string;
  location?: {
    id: string;
    name: string;
  };
}

export interface KioskSettings {
  allow_dine_in: boolean;
  allow_takeout: boolean;
  show_prices: boolean;
  require_name: boolean;
  timeout_seconds: number;
}

export interface CreateKioskInput {
  location_id: string;
  name: string;
  settings?: Partial<KioskSettings>;
}

export interface UpdateKioskInput {
  name?: string;
  location_id?: string;
  is_active?: boolean;
  settings?: Partial<KioskSettings>;
}

export interface KioskDeviceInfo {
  device_id: string;
  device_name: string;
  device_token: string;
  location_id: string;
  location_name: string;
  organization_id: string;
  settings: KioskSettings;
}

const KIOSK_TOKEN_KEY = 'blazepos-kiosk-token';
const KIOSK_DEVICE_KEY = 'blazepos-kiosk-device';

export function useKiosk() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all kiosk devices for the organization
  const { data: kioskDevices, isLoading, error, refetch } = useQuery({
    queryKey: ['kiosk-devices', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return [];

      const { data, error } = await supabase
        .from('kiosk_devices')
        .select(`
          *,
          location:locations(id, name)
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as KioskDevice[];
    },
    enabled: !!profile?.organization_id,
  });

  // Create a new kiosk device
  const createKiosk = useMutation({
    mutationFn: async (input: CreateKioskInput) => {
      if (!profile?.organization_id) {
        throw new Error('No organization found');
      }

      const { data, error } = await supabase
        .from('kiosk_devices')
        .insert({
          organization_id: profile.organization_id,
          location_id: input.location_id,
          name: input.name,
          settings: {
            allow_dine_in: true,
            allow_takeout: true,
            show_prices: true,
            require_name: false,
            timeout_seconds: 120,
            ...input.settings,
          },
        })
        .select(`
          *,
          location:locations(id, name)
        `)
        .single();

      if (error) throw error;

      // Log audit event
      await supabase.from('audit_logs').insert({
        action: 'create',
        entity_type: 'kiosk_device',
        entity_id: data.id,
        new_values: { name: input.name, location_id: input.location_id },
      });

      return data as KioskDevice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk-devices'] });
    },
  });

  // Update a kiosk device
  const updateKiosk = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateKioskInput }) => {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.location_id !== undefined) updateData.location_id = updates.location_id;
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active;
      if (updates.settings !== undefined) {
        // Merge with existing settings
        const { data: existing } = await supabase
          .from('kiosk_devices')
          .select('settings')
          .eq('id', id)
          .single();
        
        updateData.settings = { ...existing?.settings, ...updates.settings };
      }

      const { data, error } = await supabase
        .from('kiosk_devices')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          location:locations(id, name)
        `)
        .single();

      if (error) throw error;
      return data as KioskDevice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk-devices'] });
    },
  });

  // Delete a kiosk device
  const deleteKiosk = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('kiosk_devices')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Log audit event
      await supabase.from('audit_logs').insert({
        action: 'delete',
        entity_type: 'kiosk_device',
        entity_id: id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk-devices'] });
    },
  });

  // Regenerate device code (for re-registration)
  const regenerateCode = useMutation({
    mutationFn: async (deviceId: string) => {
      const { data, error } = await supabase.rpc('regenerate_kiosk_device_code', {
        p_device_id: deviceId,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk-devices'] });
    },
  });

  return {
    kioskDevices: kioskDevices ?? [],
    isLoading,
    error,
    refetch,
    createKiosk,
    updateKiosk,
    deleteKiosk,
    regenerateCode,
  };
}

// Hook for kiosk device registration and session management
export function useKioskDevice() {
  // Get stored device info
  const getStoredDevice = (): KioskDeviceInfo | null => {
    try {
      const stored = localStorage.getItem(KIOSK_DEVICE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  // Get stored token
  const getStoredToken = (): string | null => {
    return localStorage.getItem(KIOSK_TOKEN_KEY);
  };

  // Store device info
  const storeDevice = (device: KioskDeviceInfo) => {
    localStorage.setItem(KIOSK_TOKEN_KEY, device.device_token);
    localStorage.setItem(KIOSK_DEVICE_KEY, JSON.stringify(device));
  };

  // Clear stored device info
  const clearDevice = () => {
    localStorage.removeItem(KIOSK_TOKEN_KEY);
    localStorage.removeItem(KIOSK_DEVICE_KEY);
  };

  // Register device with code
  const registerDevice = async (deviceCode: string): Promise<KioskDeviceInfo> => {
    const { data, error } = await supabase.rpc('register_kiosk_device', {
      p_device_code: deviceCode.toUpperCase(),
    });

    if (error) {
      throw new Error(error.message || 'Failed to register device');
    }

    if (!data || data.length === 0) {
      throw new Error('Invalid device code');
    }

    const deviceInfo: KioskDeviceInfo = {
      device_id: data[0].device_id,
      device_name: data[0].device_name,
      device_token: data[0].device_token,
      location_id: data[0].location_id,
      location_name: data[0].location_name,
      organization_id: data[0].organization_id,
      settings: data[0].settings,
    };

    storeDevice(deviceInfo);
    return deviceInfo;
  };

  // Validate stored token
  const validateToken = async (): Promise<KioskDeviceInfo | null> => {
    const token = getStoredToken();
    if (!token) return null;

    const { data, error } = await supabase.rpc('validate_kiosk_token', {
      p_token: token,
    });

    if (error || !data || data.length === 0) {
      clearDevice();
      return null;
    }

    const deviceInfo: KioskDeviceInfo = {
      device_id: data[0].device_id,
      device_name: data[0].device_name,
      device_token: token,
      location_id: data[0].location_id,
      location_name: data[0].location_name,
      organization_id: data[0].organization_id,
      settings: data[0].settings,
    };

    // Update stored info in case settings changed
    storeDevice(deviceInfo);
    return deviceInfo;
  };

  // Check if device is registered
  const isRegistered = (): boolean => {
    return !!getStoredToken();
  };

  return {
    getStoredDevice,
    getStoredToken,
    registerDevice,
    validateToken,
    clearDevice,
    isRegistered,
  };
}






















